import type { SortOptions, SortOrder } from './sort';

import { getNestedValue } from './sort';

export function compareValuesV2(a: any, b: any, order: SortOrder = 'asc'): number {
  if (a === b) return 0;

  // Handle undefined/null values
  if (a === undefined || a === null || b === undefined || b === null) {
    if ((a === undefined || a === null) && b !== undefined && b !== null)
      return order === 'asc' ? -1 : 1;
    if ((b === undefined || b === null) && a !== undefined && a !== null)
      return order === 'asc' ? 1 : -1;
    return 0;
  }

  // Strict date validation: only Date objects or strings with date format
  const isDate = (val: any): boolean =>
    val instanceof Date ||
    (typeof val === 'string' && !isNaN(Date.parse(val)) && val.includes('-'));

  const isString = (val: any): boolean => typeof val === 'string';

  let comparison = 0;

  if (isDate(a) && isDate(b)) {
    const dateA = new Date(a).getTime();
    const dateB = new Date(b).getTime();
    comparison = dateA - dateB;
  } else if (isString(a) && isString(b)) {
    // Use localeCompare for proper string comparison with internationalization support
    comparison = a.localeCompare(b);
  } else {
    // Compare numbers or booleans
    comparison = a > b ? 1 : -1;
  }

  return order === 'asc' ? comparison : -comparison;
}

// Helper function to compare two items based on a single sort option
function compareBySingleOption<T>(a: T, b: T, option: SortOptions<T>): number {
  const { attr, order = 'asc', preferredValue, backupAttr, orderArrayAttr, orderArray } = option;
  const valueA = getNestedValue(a, attr);
  const valueB = getNestedValue(b, attr);

  // Prioritize preferred value
  if (preferredValue !== undefined) {
    if (valueA === preferredValue && valueB !== preferredValue) return -1;
    if (valueB === preferredValue && valueA !== preferredValue) return 1;
  }

  // Sort by predefined array order
  if (orderArrayAttr === attr && orderArray) {
    const orderArrayValue = orderArray[order];
    const idxA = orderArrayValue.indexOf(valueA);
    const idxB = orderArrayValue.indexOf(valueB);
    // Only compare if both values are found in the array
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
  }

  const primaryComparison = compareValuesV2(valueA, valueB, order);

  if (primaryComparison !== 0) return primaryComparison;
  if (backupAttr)
    return compareValuesV2(getNestedValue(a, backupAttr), getNestedValue(b, backupAttr), order);

  return 0;
}

export function sortByConditionV2<T>(array: T[], options: SortOptions<T>): T[] {
  return array.slice().sort((a, b) => compareBySingleOption(a, b, options));
}

// Multi-level sorting: applies multiple sort conditions in order
export function sortByConditionsV2<T>(array: T[], optionsArray: SortOptions<T>[]): T[] {
  return array.slice().sort((a, b) => {
    for (const option of optionsArray) {
      const diff = compareBySingleOption(a, b, option);
      // If items differ by this condition, return immediately. Otherwise, check next condition.
      if (diff !== 0) return diff;
    }
    return 0;
  });
}

export function sortByArrayOrderV2<T>(array: T[], order: T[], sortOrder: SortOrder = 'asc'): T[] {
  return array.slice().sort((a, b) => {
    const indexA = order.indexOf(a);
    const indexB = order.indexOf(b);
    const comparison = indexA - indexB;
    return sortOrder === 'asc' ? comparison : -comparison;
  });
}
